/**
 * Operation Renderer Registry
 *
 * Maps operation types to display configs. Each operation type has:
 * - A DetailComponent that renders operation-specific UI
 * - A getSummary() function for card/list display
 * - A getRequiredReferenceIds() function for data resolution
 * - Icon, color, and labelKey from the shared constants
 *
 * Future slots: InteractionComponent, requiresInteraction
 */

import { FC } from 'react';
import { TFunction } from 'i18next';
import type { IconProps } from '@tabler/icons-react';
import type { BatchProcessesReadmodel } from '@bett3r-dev/teselly-domain';
import { getOperationConfig, defaultOperationConfig, parseOperationRunName } from '../../../constants';

import { config as duplicationConfig } from './PublicationsDuplicationRenderer';
import { config as statusChangedConfig } from './PublicationStatusChangedRenderer';
import { config as bulkEditConfig } from './PublicationBulkEditRenderer';
import { config as operationRunConfig } from './OperationRunRenderer';
import { config as channelImportConfig } from './ChannelPublicationImportRenderer';
import { config as channelOrderImportConfig } from './ChannelOrderImportRenderer';
import { config as itemExtractionConfig } from './PublicationItemExtractionRenderer';
import { config as creationConfig } from './PublicationCreationRenderer';
import { config as priceChangedConfig } from './PublicationPriceChangedRenderer';
import { config as syncFromChannelConfig } from './PublicationSyncFromChannelRenderer';
import { config as erpItemImportConfig } from './ErpItemImportRenderer';
import { config as erpStockImportConfig } from './ErpStockImportRenderer';
import { config as erpPriceImportConfig } from './ErpPriceImportRenderer';
import { config as defaultRendererConfig } from './DefaultRenderer';

// =============================================================================
// Types
// =============================================================================

/**
 * The readmodel data field is loosely typed at the schema level because
 * it varies per operation type. Renderers cast to this to access
 * operation-specific fields safely at runtime.
 */
export type ProcessData = Record<string, any>;

export type ReferenceIds = {
  publicationIds?: string[];
  warehouseIds?: string[];
  channelIds?: string[];
};

export type ResolvedData = {
  resolvedPublications: Record<string, { id: string; title: string; thumbnail?: string; sku?: string }>;
  resolvedWarehouses: Record<string, { id: string; name: string }>;
  resolvedChannels: Record<string, { connectorId: string; name: string; icon?: string }>;
};

export type DetailRendererProps = {
  process: BatchProcessesReadmodel;
  resolved: ResolvedData;
  t: TFunction;
};

export type InteractionRendererProps = DetailRendererProps & {
  onComplete: () => void;
};

/**
 * getSummary receives:
 *   data - the process.data object
 *   t - translation function
 *   process - the full readmodel (optional, for fallback values)
 *   resolvedChannels - channel id -> resolved channel (optional)
 *   resolvedWarehouses - warehouse id -> resolved warehouse (optional)
 */
export type GetSummaryFn = (
  data: ProcessData,
  t: TFunction,
  process?: BatchProcessesReadmodel,
  resolvedChannels?: ResolvedData['resolvedChannels'],
  resolvedWarehouses?: ResolvedData['resolvedWarehouses']
) => string;

export type OperationRendererConfig = {
  icon: FC<IconProps>;
  color: string;
  labelKey: string;
  /**
   * Interpolation values for `labelKey` (operation runs only). The values are
   * themselves translation keys — always render the label via
   * `resolveOperationLabel`, never a bare `t( config.labelKey )`.
   */
  labelParams?: Record<string, string>;
  getSummary: GetSummaryFn;
  DetailComponent: FC<DetailRendererProps>;
  getRequiredReferenceIds: ( process: BatchProcessesReadmodel ) => ReferenceIds;
  /** Future: interaction wizard component */
  InteractionComponent?: FC<InteractionRendererProps>;
  /** Future: whether the process requires user interaction */
  requiresInteraction?: ( process: BatchProcessesReadmodel ) => boolean;
};

// =============================================================================
// Registry
// =============================================================================

const operationRenderers: Record<string, OperationRendererConfig> = {
  PublicationsDuplication: {
    ...getOperationConfig( 'PublicationsDuplication' ),
    ...duplicationConfig
  },
  PublicationStatusChanged: {
    ...getOperationConfig( 'PublicationStatusChanged' ),
    ...statusChangedConfig
  },
  // Emitted server-side since it shipped, never registered here — it rendered as the
  // DefaultRenderer's grey question mark labelled "Operación desconocida".
  PublicationBulkEdit: {
    ...getOperationConfig( 'PublicationBulkEdit' ),
    ...bulkEditConfig
  },
  ChannelPublicationImport: {
    ...getOperationConfig( 'ChannelPublicationImport' ),
    ...channelImportConfig
  },
  // TV1-2379 — the channel ORDER seeding run. Registered here so it renders as
  // itself rather than falling through to the DefaultRenderer's grey question mark.
  ChannelOrderImport: {
    ...getOperationConfig( 'ChannelOrderImport' ),
    ...channelOrderImportConfig
  },
  PublicationItemExtraction: {
    ...getOperationConfig( 'PublicationItemExtraction' ),
    ...itemExtractionConfig
  },
  PublicationCreation: {
    ...getOperationConfig( 'PublicationCreation' ),
    ...creationConfig
  },
  PublicationPriceChanged: {
    ...getOperationConfig( 'PublicationPriceChanged' ),
    ...priceChangedConfig
  },
  PublicationSyncFromChannel: {
    ...getOperationConfig( 'PublicationSyncFromChannel' ),
    ...syncFromChannelConfig
  },
  // ERP-ONBOARDING slice 5 — the first non-publication kind.
  ErpItemImport: {
    ...getOperationConfig( 'ErpItemImport' ),
    ...erpItemImportConfig
  },
  // ERP-ONBOARDING slice 9 — the per-warehouse bulk stock sweep. Its own renderer
  // rather than a reuse of the item one: the four buckets are shared, but only a stock
  // sweep has a COVERAGE statement ("2 of 3 ERP warehouses"), and that is the number a
  // green bar over a partial Warehouse Mapping structurally cannot express.
  ErpStockImport: {
    ...getOperationConfig( 'ErpStockImport' ),
    ...erpStockImportConfig
  },
  // ERP-ONBOARDING slice 10 — the per-price-list bulk price sweep. Its own renderer for
  // the same reason the stock one has one: the four buckets are shared, but the COVERAGE
  // statement ("2 of 5 ERP price lists") is what a green bar over a partial Price List
  // Mapping structurally cannot express, and it names price lists, not warehouses.
  ErpPriceImport: {
    ...getOperationConfig( 'ErpPriceImport' ),
    ...erpPriceImportConfig
  }
};

/**
 * Default renderer config used when the operation type is unknown.
 * Merges the default visual config with the fallback renderer.
 */
const defaultRenderer: OperationRendererConfig = {
  ...defaultOperationConfig,
  ...defaultRendererConfig
};

/**
 * Get the renderer config for an operation name.
 * Falls back to DefaultRenderer for unknown operation types,
 * ensuring the UI never crashes when a new type is added server-side.
 */
export const getRenderer = ( operationName: string ): OperationRendererConfig => {
  const registered = operationRenderers[operationName];
  if ( registered ) return registered;

  // The operation-run family (`Operation:<operationType>:<phase>`) is matched by
  // PREFIX, because those names are minted at projection time from the run's type and
  // phase and so are not enumerable here. Without this branch every operation run —
  // bulk publish included — falls to `defaultRenderer` and renders untitled.
  if ( parseOperationRunName( operationName )) {
    return {
      ...getOperationConfig( operationName ),
      ...operationRunConfig
    };
  }

  return defaultRenderer;
};

/**
 * Get all registered operation names (useful for testing/debugging).
 */
export const getRegisteredOperations = (): string[] =>
  Object.keys( operationRenderers );
